<script setup lang="ts">
import { useTranslate } from "addeus-common-library/stores/translate";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { computed, onMounted, ref, watch } from "vue";
// COMPONENTS
import Title from "../../../components/website/LayoutTitleSection.vue";
import Who from "../../../components/website/LayoutForWho.vue";
import ImageHeader from "../../../components/website/ImageHeader.vue";
import VFlex from "addeus-common-library/components/base/flex/VFlex.vue";
import ContactForm from "../../../components/website/ContactForm.vue";
// MEDIA
import VideoHeader from "../../../assets/image/eventYour/eventYour-header.mp4";
import IconEventyourAdminTitle from "../../../assets/image/eventYour/icons/icon-eventYour-admin-title.svg";
import Track from "../../../assets/image/discover/history/discover-history-icon1.svg";
import Puzzle from "../../../assets/icons/icon-discover-black-2.svg";
import Kart_Up from "../../../assets/image/discover/history/discover-history-icon2.svg";
import Community from "../../../assets/image/discover/history/discover-history-icon3.svg";
// ENTERPRISE
import ACCORD from "../../../assets/image/eventYour/entreprises/ACCOR_HOTEL.svg";
import ALPINE from "../../../assets/image/eventYour/entreprises/ALPINE.svg";
import DRIVING from "../../../assets/image/eventYour/entreprises/DRIVING_EVOLUTION.svg";
import FASHION from "../../../assets/image/eventYour/entreprises/FASHION_WEEK.svg";
import HOPSCOTCH from "../../../assets/image/eventYour/entreprises/HOPSCOTCH_EVENT.svg";
import INDIGO from "../../../assets/image/eventYour/entreprises/INDIGO_BMW.svg";
import LOHR from "../../../assets/image/eventYour/entreprises/LOHR.svg";
import LOMBARD from "../../../assets/image/eventYour/entreprises/LOMBARD_ODIER.svg";
import MERCEDES from "../../../assets/image/eventYour/entreprises/MERCEDES_BENZ.svg";
import MONDIAL from "../../../assets/image/eventYour/entreprises/MONDIAL_DE_L_AUTO.svg";
import MUSEE from "../../../assets/image/eventYour/entreprises/MUSEE_NATIONAL_DE_L_AUTO.svg";
import NAPA from "../../../assets/image/eventYour/entreprises/NAPA.svg";
import PARC from "../../../assets/image/eventYour/entreprises/PARC_DES_PRINCES.svg";
import PASSE from "../../../assets/image/eventYour/entreprises/PASSE_MURAILLE.svg";
import PEUGEOT from "../../../assets/image/eventYour/entreprises/PEUGEOT.svg";
import SALOMON from "../../../assets/image/eventYour/entreprises/SALOMON.svg";
import STELLANTIS from "../../../assets/image/eventYour/entreprises/STELLANTIS.svg";
import VILLA from "../../../assets/image/eventYour/entreprises/VILLA_MAGNA_MADRID.svg";
import WRC from "../../../assets/image/eventYour/entreprises/WRC.svg";
// ADMINISTRATION
import ARGENTEUIL from "../../../assets/image/eventYour/administrations/ARGENTEUIL.svg";
import BERRE from "../../../assets/image/eventYour/administrations/BERRE_L_ETANG.svg";
import BOBIGNY from "../../../assets/image/eventYour/administrations/BOBIGNY.svg";
import CARCASSONNE from "../../../assets/image/eventYour/administrations/CARCASSONNE.svg";
import CARRY from "../../../assets/image/eventYour/administrations/CARRY_LE_ROUET.svg";
import COURCHEVEL from "../../../assets/image/eventYour/administrations/COURCHEVEL.svg";
import FOS from "../../../assets/image/eventYour/administrations/FOS_SUR_MER.svg";
import ISTRES from "../../../assets/image/eventYour/administrations/ISTRES.svg";
import MENDE from "../../../assets/image/eventYour/administrations/MENDE.svg";
import MIRAMAS from "../../../assets/image/eventYour/administrations/MIRAMAS.svg";
import PATINOIRE from "../../../assets/image/eventYour/administrations/PATINOIRE_DE_THONEX.svg";
// CARR PORTRAIT
import CP1 from "../../../assets/image/eventYour/carrousel_p/1.png";
import CP2 from "../../../assets/image/eventYour/carrousel_p/2.png";
import CP3 from "../../../assets/image/eventYour/carrousel_p/3.png";
import CP4 from "../../../assets/image/eventYour/carrousel_p/4.png";
import CP5 from "../../../assets/image/eventYour/carrousel_p/5.png";
import CP6 from "../../../assets/image/eventYour/carrousel_p/6.png";
// CARR LAND
import CL1 from "../../../assets/image/eventYour/carrousel_l/1.png";
import CL2 from "../../../assets/image/eventYour/carrousel_l/2.png";
import CL3 from "../../../assets/image/eventYour/carrousel_l/3.png";
import CL4 from "../../../assets/image/eventYour/carrousel_l/4.png";
import CL5 from "../../../assets/image/eventYour/carrousel_l/5.png";
import CL6 from "../../../assets/image/eventYour/carrousel_l/6.png";
import Translate from "addeus-common-library/components/base/translation/Translate.vue";
import { useRouter } from "vue-router";
import { useCollection } from "addeus-common-library/stores/firestore";
import { B2BNotice as B2BNoticeModel } from "../../../models/notice";
import B2BNotice from "../../../components/website/B2BNotice.vue";
import type { FlickingOptions } from "@egjs/vue3-flicking";
import { AutoPlay, Arrow, Pagination } from "@egjs/flicking-plugins";
import "@egjs/vue3-flicking/dist/flicking.css";
import Flicking from "@egjs/vue3-flicking";
import { useSeoMeta } from "../../../composables/useSeoMeta";

const breakpoints = useBreakpoints(breakpointsTailwind);
const smallerThanXl = breakpoints.smallerOrEqual("xl");

const { currentRoute, push } = useRouter();

let contact: HTMLElement | null = null;
onMounted(() => {
    contact = document.getElementById("contact");
});

watch(
    currentRoute,
    (value) => {
        if (value.hash === "#contact") {
            scrollToContact();
        }
    },
    { immediate: true },
);

const { setTranslateNamespace } = useTranslate();
setTranslateNamespace(".eventYour");

useSeoMeta({
    title: "Location karting électrique événement | Entreprises et collectivités",
    description:
        "Location de karting électrique itinérant clé en main : séminaires entreprise, fêtes de quartier, animations mairie, salons professionnels. Speed2Max installe une piste sur votre site partout en France.",
    path: "/event-your",
    breadcrumbs: [
        { name: "Accueil", path: "/" },
        { name: "Événement chez vous", path: "/event-your" },
    ],
    schemaOrg: [
        {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Location karting électrique événementiel - Speed2Max",
            description:
                "Location et installation de piste de karting électrique itinérant pour entreprises, collectivités, mairies et associations. Prestation clé en main.",
            url: "https://speed2max.com/event-your",
            provider: {
                "@type": "Organization",
                name: "Speed2Max",
                url: "https://speed2max.com",
            },
            serviceType: "Location de karting événementiel",
            areaServed: "France",
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
                {
                    "@type": "Question",
                    name: "Speed2Max peut-il installer une piste de karting chez nous ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "Oui, Speed2Max installe des pistes de karting électrique mobiles clé en main sur votre site : parkings, halls, espaces extérieurs. Prestation disponible pour entreprises, collectivités et associations dans toute la France.",
                    },
                },
                {
                    "@type": "Question",
                    name: "Quels types d'événements peut-on organiser avec le karting mobile ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "Le karting mobile convient aux séminaires d'entreprise, fêtes municipales, événements associatifs, salons professionnels et tout type de manifestation. Mercedes, Peugeot, Stellantis et le Mondial de l'Auto nous ont déjà fait confiance.",
                    },
                },
                {
                    "@type": "Question",
                    name: "Quelle surface faut-il pour installer une piste mobile ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "La configuration de la piste s'adapte à votre espace. Contactez-nous pour une étude personnalisée gratuite de votre lieu.",
                    },
                },
            ],
        },
    ],
});

const enterprises = [
    ACCORD,
    ALPINE,
    DRIVING,
    FASHION,
    HOPSCOTCH,
    INDIGO,
    LOHR,
    LOMBARD,
    MERCEDES,
    MONDIAL,
    MUSEE,
    NAPA,
    PARC,
    PASSE,
    PEUGEOT,
    SALOMON,
    STELLANTIS,
    VILLA,
    WRC,
];
const administration = [
    ARGENTEUIL,
    BERRE,
    BOBIGNY,
    CARCASSONNE,
    CARRY,
    COURCHEVEL,
    FOS,
    ISTRES,
    MENDE,
    MIRAMAS,
    PATINOIRE,
];
const portrait = [CP1, CP2, CP3, CP4, CP5, CP6];
const landscape = [CL1, CL2, CL3, CL4, CL5, CL6];
const flickingConfigImages: Partial<FlickingOptions> = {
    renderOnlyVisible: true,
    resizeOnContentsReady: true,
    bound: false,
    circular: true,
    panelsPerView: 1,
    horizontal: true,
    align: "prev",
};
const apImageConfig = { animationDuration: 750, duration: 2750, stopOnHover: false };
const portraitPlugins = [
    new AutoPlay(apImageConfig),
    new Arrow(),
    new Pagination({ type: "bullet" }),
];
const landscapePlugins = [
    new AutoPlay(apImageConfig),
    new Arrow(),
    new Pagination({ type: "bullet" }),
];
const flickingConfigLogo = computed<Partial<FlickingOptions>>(() => {
    return {
        renderOnlyVisible: false,
        resizeOnContentsReady: true,
        circular: true,
        panelsPerView: smallerThanXl.value ? 2 : 5,
        horizontal: true,
        align: "center",
    };
});
const apLogoConfig = { animationDuration: 250, duration: 1500, stopOnHover: false };
const enperprisePlugins = [new AutoPlay(apLogoConfig), new Arrow()];
const administrationPlugins = [new AutoPlay(apLogoConfig), new Arrow()];
const flickingConfigNotice = computed<Partial<FlickingOptions>>(() => {
    return {
        renderOnlyVisible: true,
        adaptive: true,
        circular: false,
        bound: true,
        horizontal: true,
        panelPerView: smallerThanXl.value ? 1 : 2,
        resizeOnContentsReady: true,
        align: "prev",
    } as Partial<FlickingOptions>;
});
type APConfig = {
    animationDuration: number;
    duration: number;
    stopOnHover: boolean;
    direction: "NEXT" | "PREV" | undefined;
};
const apNoticeConfig = ref<Partial<APConfig>>({
    animationDuration: 1000,
    duration: 20_000,
    stopOnHover: false,
    direction: "NEXT",
});
const noticesPlugins = computed(() => {
    return [new AutoPlay(apNoticeConfig.value), new Arrow()];
});

function onNoticesEdgeReached() {
    const newValue = apNoticeConfig.value.direction === "PREV" ? "NEXT" : "PREV";
    apNoticeConfig.value.direction = newValue;
}

function scrollToContact() {
    if (contact === null) {
        return;
    }
    contact.scrollIntoView({ behavior: "smooth" });
}

function goToContact() {
    if (currentRoute.value.hash === "") {
        void push({ hash: "#contact" });
    } else {
        scrollToContact();
    }
}

const notices = useCollection(B2BNoticeModel, { limit: 10 });
</script>

<template>
    <VFlex flex-direction="column">
        <VFlex flex-direction="column" class="has-background-dark">
            <ImageHeader :hide-background="true" :secondary="false">
                <template #image>
                    <div
                        class="video-player-container"
                        style="overflow: hidden"
                        :style="{ margin: smallerThanXl ? '5rem auto' : '0 auto' }">
                        <video playsinline loop muted autoplay crossorigin="anonymous">
                            <source :src="VideoHeader" />
                        </video>
                    </div>
                </template>

                <template #title>
                    <div>
                        <Translate path=".title" />
                    </div>
                    <div class="has-text-primary">
                        <Translate path=".title2" />
                    </div>
                </template>
                <template #icon>
                    <img
                        :src="IconEventyourAdminTitle"
                        alt="Icône karting mobile pour collectivités et entreprises" />
                </template>
            </ImageHeader>
            <Who class="mx-auto">
                <template #title>
                    <span :class="{ 'is-size-4': smallerThanXl }">
                        <Translate path=".title"></Translate>
                    </span>
                </template>
                <template #content>
                    <span
                        class="is-size-5 has-text-weight-normal"
                        :class="{ 'is-size-6': smallerThanXl }">
                        <Translate path=".content1" />&nbsp;
                        <Translate path=".content2" />
                    </span>
                </template>
                <template #icon>
                    <img
                        src="/images/kart/track-icon.png"
                        alt="Icône piste de karting mobile" />
                </template>
                <template #titleP1>
                    <span class="is-italic">
                        <Translate path=".titleP11" />
                        <br />
                        <Translate path=".titleP12" />
                    </span>
                </template>
                <template #titleP2>
                    <br />
                    <span class="is-italic">
                        <Translate path=".titleP2" />
                    </span>
                </template>
            </Who>
            <div
                :class="{
                    'ml-auto': !smallerThanXl,
                    'mr-6': !smallerThanXl,
                    'mx-auto': smallerThanXl,
                    'mb-4': smallerThanXl,
                }">
                <button
                    class="button ml-6 mr-6 mb-3 is-uppercase is-family-code"
                    style="background-color: hsl(153deg 100% 35%); color: white"
                    @click="goToContact()">
                    <Translate path=".contact" />
                </button>
            </div>
            <TranslateNamespace path=".bullet-point">
                <VFlex
                    flex-direction="column"
                    class="px-6"
                    :class="{ 'mx-6': !smallerThanXl }">
                    <h3 class="is-size-5 is-uppercase" style="line-height: 2.5rem">
                        <Translate path=".title" />
                        <br />
                        <span
                            class="is-size-2 has-text-primary is-uppercase is-family-code"
                            :class="{ 'is-size-4': smallerThanXl }">
                            <Translate path=".subtitle">.subtitle</Translate>
                        </span>
                    </h3>
                    <div class="is-size-5" :class="{ 'is-size-6': smallerThanXl }">
                        <div class="is-flex is-flex-direction-row">
                            <img
                                class="image is-32x32 my-auto mr-3"
                                src="../../../assets/image/eventYour/icons/bullet-point.png"
                                alt="lightning" />
                            <p class="has-text-white is-flex-grow-1">
                                <span class="has-text-weight-bold">
                                    <Translate>.enterprise.head</Translate>
                                </span>
                                <Translate>.enterprise.content</Translate>
                            </p>
                        </div>
                        <div class="is-flex is-flex-direction-row">
                            <img
                                class="image is-32x32 my-auto mr-3"
                                src="../../../assets/image/eventYour/icons/bullet-point.png"
                                alt="lightning" />
                            <p class="has-text-white is-flex-grow-1">
                                <span class="has-text-weight-bold">
                                    <Translate>.community.head</Translate>
                                </span>
                                <Translate>.community.content</Translate>
                            </p>
                        </div>
                        <div class="is-flex is-flex-direction-row">
                            <img
                                class="image is-32x32 my-auto mr-3"
                                src="../../../assets/image/eventYour/icons/bullet-point.png"
                                alt="lightning" />
                            <p class="has-text-white is-flex-grow-1">
                                <span class="has-text-weight-bold">
                                    <Translate>.association.head</Translate>
                                </span>
                                <Translate>.association.content</Translate>
                            </p>
                        </div>
                    </div>
                </VFlex>
            </TranslateNamespace>
            <VFlex
                :flex-direction="smallerThanXl ? 'column' : 'row'"
                justify-content="space-around"
                :class="{
                    'mx-auto': smallerThanXl,
                    'm-6': !smallerThanXl,
                    'mb-6': smallerThanXl,
                }">
                <Flicking
                    :options="flickingConfigImages"
                    :viewport-tag="'div'"
                    :camera-tag="'div'"
                    :camera-class="''"
                    :plugins="portraitPlugins"
                    style="width: 80%; max-width: 400px"
                    class="my-6"
                    :class="{ 'mx-6': !smallerThanXl, 'mx-auto': smallerThanXl }">
                    <div
                        v-for="(image, index) in portrait"
                        :key="index"
                        style="width: 100%">
                        <img
                            class="image"
                            :src="image"
                            alt="Événement karting mobile Speed2Max en action" />
                    </div>
                    <template #viewport>
                        <span class="flicking-arrow-prev is-thin is-circle"></span>
                        <span class="flicking-arrow-next is-thin is-circle"></span>
                        <div class="flicking-pagination"></div>
                    </template>
                </Flicking>
                <TranslateNamespace path=".performance">
                    <VFlex
                        flex-direction="column"
                        :class="{
                            'mx-auto': smallerThanXl,
                            'mb-6': !smallerThanXl,
                            'mr-6': !smallerThanXl,
                        }"
                        :style="{ 'max-width': smallerThanXl ? '80%' : '38%' }">
                        <h3
                            class="is-size-5 has-text-right is-uppercase mb-4"
                            style="line-height: 2.5rem">
                            <Translate>.title</Translate>
                            <br />
                            <span
                                class="is-size-2 has-text-primary is-uppercase is-family-code"
                                :class="{ 'is-size-3': smallerThanXl }">
                                <Translate>.subtitle</Translate>
                            </span>
                        </h3>
                        <div class="card my-2 is-flex is-flex-direction-row">
                            <img
                                class="card-image my-auto image is-64x64 p-3"
                                :src="Track"
                                alt="logo" />
                            <p
                                style="line-height: 1.5rem"
                                class="card-content is-flex-grow-1 is-size-6 p-3 is-uppercase has-text-weight-semibold has-text-dark">
                                <Translate>.content1</Translate>
                            </p>
                        </div>
                        <div class="card my-2 is-flex is-flex-direction-row">
                            <img
                                class="card-image my-auto image is-64x64 p-3"
                                :src="Kart_Up"
                                alt="logo" />
                            <p
                                style="line-height: 1.5rem"
                                class="card-content is-size-6 p-3 is-uppercase has-text-weight-semibold has-text-dark">
                                <Translate>.content2</Translate>
                            </p>
                        </div>
                        <div class="card my-2 is-flex is-flex-direction-row">
                            <img
                                class="card-image my-auto image is-64x64 p-3"
                                :src="Community"
                                alt="logo" />
                            <p
                                style="line-height: 1.5rem"
                                class="card-content is-size-6 p-3 is-uppercase has-text-weight-semibold has-text-dark">
                                <Translate>.content3</Translate>
                            </p>
                        </div>
                        <div class="card my-2 is-flex is-flex-direction-row">
                            <img
                                class="card-image my-auto image is-64x64 p-3"
                                :src="Puzzle"
                                alt="logo" />
                            <p
                                style="line-height: 1.5rem"
                                class="card-content is-size-6 p-3 is-uppercase has-text-weight-semibold has-text-dark">
                                <Translate>.content4</Translate>
                            </p>
                        </div>
                    </VFlex>
                </TranslateNamespace>
            </VFlex>
        </VFlex>
        <!-- END OF DARK THEME-->
        <TranslateNamespace path=".trust">
            <VFlex
                flex-direction="column"
                class="mt-6"
                :class="{ 'mx-6': !smallerThanXl }">
                <h3
                    class="is-size-2 has-text-dark is-uppercase is-family-code ml-6"
                    :class="{ 'is-size-3': smallerThanXl }">
                    <Translate>.title</Translate>&nbsp;
                    <span class="has-text-primary">
                        <Translate>.subtitle</Translate>
                    </span>
                </h3>
                <Title :secondary="false" :third="false">
                    <template #titleP1>
                        <span class="is-size-2" :class="{ 'is-size-3': smallerThanXl }">
                            <Translate path=".enterprise"></Translate>
                        </span>
                    </template>
                </Title>
                <Flicking
                    :options="flickingConfigLogo"
                    :viewport-tag="'div'"
                    :camera-tag="'div'"
                    :camera-class="''"
                    :plugins="enperprisePlugins"
                    class="mx-6 mb-4">
                    <div
                        v-for="(image, index) in enterprises"
                        :key="index"
                        :style="{ width: smallerThanXl ? '50%' : '20%' }">
                        <img
                            style="height: 90px"
                            :src="image"
                            alt="Logo entreprise partenaire Speed2Max" />
                    </div>
                    <template #viewport>
                        <span class="flicking-arrow-prev is-thin is-circle"></span>
                        <span class="flicking-arrow-next is-thin is-circle"></span>
                    </template>
                </Flicking>
                <Title :secondary="false" :third="false">
                    <template #titleP1>
                        <span class="is-size-2" :class="{ 'is-size-3': smallerThanXl }">
                            <Translate path=".administration"></Translate>
                        </span>
                    </template>
                </Title>
                <Flicking
                    :options="flickingConfigLogo"
                    :viewport-tag="'div'"
                    :camera-tag="'div'"
                    :camera-class="''"
                    :plugins="administrationPlugins"
                    class="mx-6">
                    <div
                        v-for="(image, index) in administration"
                        :key="index"
                        :style="{ width: smallerThanXl ? '50%' : '20%' }">
                        <img
                            style="height: 90px"
                            :src="image"
                            alt="Logo collectivité partenaire Speed2Max" />
                    </div>
                    <template #viewport>
                        <span class="flicking-arrow-prev is-thin is-circle"></span>
                        <span class="flicking-arrow-next is-thin is-circle"></span>
                    </template>
                </Flicking>
                <button
                    class="button is-medium my-6 mx-auto is-uppercase is-family-code"
                    style="background-color: hsl(153deg 100% 35%); color: white"
                    @click="goToContact()">
                    <Translate>.contact</Translate>
                </button>
                <Flicking
                    :options="flickingConfigImages"
                    :viewport-tag="'div'"
                    :camera-tag="'div'"
                    :camera-class="''"
                    :plugins="landscapePlugins"
                    style="width: 80%; max-width: 800px"
                    class="mx-auto">
                    <div
                        v-for="(image, index) in landscape"
                        :key="index"
                        style="width: 100%">
                        <img
                            class="image"
                            :src="image"
                            alt="Événement karting mobile Speed2Max en action" />
                    </div>
                    <template #viewport>
                        <span class="flicking-arrow-prev is-thin is-circle"></span>
                        <span class="flicking-arrow-next is-thin is-circle"></span>
                        <div class="flicking-pagination"></div>
                    </template>
                </Flicking>
                <div class="mx-6 my-3">
                    <h2 class="is-size-2 has-text-dark has-text-weight-bold mb-2 mt-5">
                        <Translate>.notice.title</Translate>&nbsp;
                        <span class="has-text-primary">
                            <Translate path=".notice.title-2"></Translate>
                        </span>
                    </h2>
                    <Flicking
                        :config="flickingConfigNotice"
                        :viewport-tag="'div'"
                        :viewport-class="'is-flex has-background-warning'"
                        :camera-tag="'div'"
                        :camera-class="'is-align-items-start'"
                        :plugins="noticesPlugins"
                        @reach-edge="onNoticesEdgeReached">
                        <B2BNotice
                            v-for="(notice, index) in notices"
                            :key="index"
                            style="margin: 0 2.5%"
                            :model-value="notice" />
                        <template #viewport>
                            <span class="flicking-arrow-prev is-thin is-circle"></span>
                            <span class="flicking-arrow-next is-thin is-circle"></span>
                        </template>
                    </Flicking>
                </div>
                <Title :secondary="false" :third="false">
                    <template #titleP1>
                        <span class="is-size-2">
                            <Translate path=".contact1"></Translate>
                        </span>
                    </template>
                    <template #titleP2>
                        <span class="is-size-2">
                            <Translate path=".contact2"></Translate>
                        </span>
                    </template>
                </Title>
                <p
                    class="has-text-black mx-6 is-size-6 mb-6 has-text-weight-medium"
                    style="line-height: 1.2rem">
                    <translate>.details</translate>
                </p>
                <div id="contact"></div>
                <TranslateNamespace path=".form">
                    <ContactForm class="mx-6 mb-6"></ContactForm>
                </TranslateNamespace>
            </VFlex>
        </TranslateNamespace>
        <Footer></Footer>
    </VFlex>
</template>

<style lang="scss">
.flicking-pagination {
    position: absolute;
    left: 0;
    bottom: 20px;
    width: 100%;
    text-align: center;
    z-index: 2;
}

.flicking-pagination-bullets,
.flicking-pagination-scroll {
    font-size: 0;
}

.flicking-pagination-scroll {
    left: 50%;
    transform: translate(-50%);
    white-space: nowrap;
    overflow: hidden;
}

.flicking-pagination-scroll .flicking-pagination-slider {
    transition: 0.2s transform;
}

.flicking-pagination-scroll.flicking-pagination-uninitialized .flicking-pagination-slider,
.flicking-pagination-scroll.flicking-pagination-uninitialized
    .flicking-pagination-bullet {
    transition: none;
}

.flicking-pagination-bullet {
    display: inline-block;
    width: 10px;
    height: 10px;
    margin: 0 4px;
    border-radius: 50%;
    background-color: white;
    cursor: pointer;
    font-size: 1rem;
}

.flicking-pagination-scroll .flicking-pagination-bullet {
    vertical-align: middle;
    position: relative;
    transition: 0.2s transform;
}

.flicking-pagination-bullet-active {
    background-color: $primary;
}

.flicking-pagination-scroll .flicking-pagination-bullet {
    vertical-align: middle;
    position: relative;
    transition: 0.2s transform, 0.2s left;
    transform: scale(0);
}

.flicking-pagination-scroll .flicking-pagination-bullet-active {
    transform: scale(1);
}

.flicking-pagination-scroll .flicking-pagination-bullet-prev,
.flicking-pagination-scroll .flicking-pagination-bullet-next {
    transform: scale(0.66);
}

.flicking-pagination-scroll .flicking-pagination-bullet-prev2,
.flicking-pagination-scroll .flicking-pagination-bullet-next2 {
    transform: scale(0.33);
}
</style>

<style lang="scss" scoped>
.flicking-arrow-prev,
.flicking-arrow-next {
    position: absolute;
    top: 50%;
    width: 32px;
    height: 32px;
    cursor: pointer;
    transform: translateY(-50%);
    z-index: 2;
}

.flicking-arrow-prev.is-circle,
.flicking-arrow-next.is-circle {
    background-color: rgb(255 255 255 / 50%);
    border-radius: 50%;
}

.flicking-arrow-disabled.is-circle {
    background-color: rgb(255 255 255 / 10%);
}

.flicking-arrow-prev.is-circle::before,
.flicking-arrow-prev.is-circle::after,
.flicking-arrow-next.is-circle::before,
.flicking-arrow-next.is-circle::after {
    background-color: $dark;
}

.flicking-arrow-prev {
    left: 10px;
}

.flicking-arrow-next {
    right: 10px;
}

.flicking-arrow-prev.is-outside {
    left: -54px;
}

.flicking-arrow-next.is-outside {
    right: -54px;
}

.flicking-arrow-prev,
.flicking-arrow-next {
    &::before,
    &::after {
        content: "";
        width: 12px;
        height: 6px;
        position: absolute;
        background-color: white;
    }
}

.flicking-arrow-prev::before {
    top: 50%;
    left: 11px;
    transform: rotate(-45deg);
    transform-origin: 0% 50%;
}

.flicking-arrow-prev::after {
    top: calc(50% - 4px);
    left: 11px;
    transform: rotate(45deg);
    transform-origin: 0% 50%;
}

.flicking-arrow-next::before {
    top: 50%;
    right: 11px;
    transform: rotate(45deg);
    transform-origin: 100% 50%;
}

.flicking-arrow-next::after {
    top: calc(50% - 4px);
    right: 11px;
    transform: rotate(-45deg);
    transform-origin: 100% 50%;
}

.flicking-arrow-disabled,
.flicking-arrow-disabled {
    cursor: default;
}

.flicking-arrow-disabled::before,
.flicking-arrow-disabled::after,
.flicking-arrow-disabled::before,
.flicking-arrow-disabled::after {
    background-color: #e6e6e6;
}

.flicking-arrow-prev.is-thin::before,
.flicking-arrow-prev.is-thin::after,
.flicking-arrow-next.is-thin::before,
.flicking-arrow-next.is-thin::after {
    height: 3px;
}

.flicking-arrow-prev.is-thin::after,
.flicking-arrow-next.is-thin::after {
    top: calc(50% - 2px);
}

.flicking-viewport {
    transition: height 500ms;
}

body {
    scroll-behavior: smooth;
}

p.card-content {
    height: fit-content;
    margin: auto;
}

.is-160x90 {
    width: 160px;
    height: 90px;
}

.object-carousel {
    width: 50%;
}

.carousel {
    --vc-pgn-background-color: rgb(255 255 255 / 70%);
    --vc-pgn-active-color: rgb(255 255 255 / 100%);
    --vc-nav-background: rgb(255 255 255 / 70%);
    --vc-nav-border-radius: 100%;

    img {
        border-radius: 8px;
        object-fit: cover;
    }
}
</style>
